// Sprint numbers and dates: amounts in a sprint's unit, durations like "2h 30m", calendar dates in a project's zone.
import type { TFunction } from "i18next";
import i18n from "@/i18n";
import type { EstimateUnit } from "@/api/types/EstimateUnit";
import type { Sprint } from "@/api/types/Sprint";
import type { TaskSummary } from "@/api/types/TaskSummary";
import type { Weekday } from "@/api/types/Weekday";

export const WEEKDAYS: Weekday[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
export const POINT_PICKS = [1, 2, 3, 5, 8, 13, 21];
export const HOUR_PICKS = [30, 60, 120, 240, 480, 960];

/** Minutes as "2h 30m", "45m", "3h" (0 → "0h"). */
export function formatMinutes(min: number): string {
  const m = Math.round(Math.abs(min));
  const h = Math.floor(m / 60);
  const r = m % 60;
  const s = h && r ? `${h}h ${r}m` : h ? `${h}h` : r ? `${r}m` : "0h";
  return min < 0 ? `−${s}` : s;
}

/**
 * "2h 30m", "2.5h", "90m", "1h30", "2" (hours) → minutes; null when it can't be read.
 * `d` counts 8 working hours, as Jira does.
 */
export function parseDuration(text: string): number | null {
  const s = text.trim().toLowerCase().replace(",", ".");
  if (!s) return null;
  if (/^\d+(\.\d+)?$/.test(s)) return Math.round(parseFloat(s) * 60);
  const re = /(\d+(?:\.\d+)?)\s*(d|h|m)/g;
  let total = 0;
  let used = "";
  let m: RegExpExecArray | null;
  while ((m = re.exec(s))) {
    const n = parseFloat(m[1]!);
    total += m[2] === "d" ? n * 480 : m[2] === "h" ? n * 60 : n;
    used += m[0];
  }
  // "1h30": trailing bare minutes.
  const tail = s.replace(re, "").trim();
  if (/^\d+$/.test(tail) && used.includes("h")) {
    total += parseInt(tail, 10);
  } else if (tail) return null;
  return used ? Math.round(total) : null;
}

/** A number without trailing zeros: 2, 2.5, 0.25. */
export const num = (n: number) => String(Math.round(n * 100) / 100);

/** An amount in a sprint's unit, with the unit: "5 pts", "2h 30m", "3 tasks". */
export function formatAmount(unit: EstimateUnit, n: number, t: TFunction): string {
  if (unit === "hours") return formatMinutes(n);
  if (unit === "points") return t("sprints.unit.pts", { n: num(n) });
  return t("sprints.unit.tasks", { n: num(n) });
}

/** Just the number for a unit (hours as a duration). */
export const formatBare = (unit: EstimateUnit, n: number) => (unit === "hours" ? formatMinutes(n) : num(n));

/** A task's estimate in a unit; null when it has none (`none` counts every task as 1). */
export function taskEstimate(task: Pick<TaskSummary, "estimate_points" | "estimate_minutes">, unit: EstimateUnit): number | null {
  if (unit === "points") return task.estimate_points;
  if (unit === "hours") return task.estimate_minutes;
  return 1;
}

// ───────────── Dates ─────────────

/** Today as YYYY-MM-DD in a time zone (the browser's when it's unknown). */
export function todayIn(tz: string | undefined): string {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  } catch {
    return new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  }
}

export function browserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function allZones(): string[] {
  try {
    const list = Intl.supportedValuesOf("timeZone");
    return list.includes("UTC") ? list : ["UTC", ...list];
  } catch {
    return ["UTC"];
  }
}

const ymd = (s: string) => {
  const [y, m, d] = s.split("-").map(Number);
  return Date.UTC(y!, (m ?? 1) - 1, d ?? 1);
};

/** Calendar days from `a` to `b` (both YYYY-MM-DD). */
export const daysBetween = (a: string, b: string) => Math.round((ymd(b) - ymd(a)) / 86_400_000);

/** YYYY-MM-DD plus `n` days. */
export function addDaysYmd(s: string, n: number): string {
  const d = new Date(ymd(s) + n * 86_400_000);
  return d.toISOString().slice(0, 10);
}

const UZ_MONTHS = ["yan", "fev", "mar", "apr", "may", "iyn", "iyl", "avg", "sen", "okt", "noy", "dek"];

/** "3 Nov" (with the year when it isn't this year's). */
export function shortDay(s: string, withYear = false): string {
  const d = new Date(ymd(s));
  const year = withYear || d.getUTCFullYear() !== new Date().getFullYear();
  if (i18n.language.startsWith("uz")) return `${d.getUTCDate()}-${UZ_MONTHS[d.getUTCMonth()]}${year ? `, ${d.getUTCFullYear()}` : ""}`;
  return d.toLocaleDateString(i18n.language, { day: "numeric", month: "short", timeZone: "UTC", ...(year ? { year: "numeric" } : {}) });
}

/** "3 Nov – 14 Nov". */
export const dateRange = (s: { start_date: string; end_date: string }) => `${shortDay(s.start_date)} – ${shortDay(s.end_date)}`;

/** Where a sprint stands in time: "6 days left", "Last day", "Starts in 3 days", "Ended 2 days ago". */
export function timeLeft(s: Sprint, t: TFunction): string | null {
  const today = todayIn(s.timezone);
  if (s.state === "completed") return null;
  if (s.state === "planned") {
    const n = daysBetween(today, s.start_date);
    return n > 0 ? t("sprints.startsIn", { count: n }) : t("sprints.startsToday");
  }
  const n = daysBetween(today, s.end_date);
  if (n < 0) return t("sprints.endedAgo", { count: -n });
  if (n === 0) return t("sprints.lastDay");
  return t("sprints.daysLeft", { count: n + 1 });
}

